import React from 'react'
import { Text, Link, Heading, Picture } from '../../components/index.js'
import { cn, getLocalizedPath } from '../../helpers/utils.js'
import { DownArrow } from './icons/index.js'
import { SeriesProductItem } from './SidebarDropdown.js'
import { useAiuiContext } from '../AiuiProvider/index.js'

/**
 * 移动端菜单项
 */
export const MenuItem = ({
  label,
  href,
  onClick,
  active,
  icon,
  className,
}: {
  label: string
  href?: string
  onClick?: () => void
  active?: boolean
  icon?: string
  className?: string
}) => {
  const content = (
    <>
      <div className="flex items-center gap-4">
        <Text html={label} className={cn('text-sm font-bold leading-[1.4]', { underline: icon })} />
        {icon && <Text html={icon} aria-hidden="true" />}
      </div>
      <svg
        className={`size-5 ${active ? 'rotate-90' : ''}`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </>
  )

  return href ? (
    <Link
      href={href}
      className={cn('flex cursor-pointer items-center justify-between py-4 no-underline', className)}
      onClick={onClick}
    >
      {content}
    </Link>
  ) : (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between border-0 bg-transparent py-4 text-left',
        className
      )}
      aria-expanded={active}
    >
      {content}
    </button>
  )
}

/**
 * 移动端子分类标题组件
 */
export const SubSubCategoryItemComp = ({
  matchSeriesMetadata,
  onSubSubCategoryItemClick,
  expanded,
}: {
  matchSeriesMetadata: any
  onSubSubCategoryItemClick?: () => void
  expanded?: boolean
}) => {
  const content = (
    <>
      {matchSeriesMetadata?.label && (
        <Text html={matchSeriesMetadata?.label} className="text-sm font-bold leading-[1.4]" />
      )}
      {onSubSubCategoryItemClick && (
        <DownArrow
          aria-hidden="true"
          className={cn('size-5', {
            ['rotate-180']: expanded,
          })}
        />
      )}
    </>
  )

  return onSubSubCategoryItemClick ? (
    <button
      className="tablet:pt-0 flex w-full cursor-pointer items-center justify-between border-0 bg-transparent py-4 text-left"
      onClick={onSubSubCategoryItemClick}
      aria-expanded={expanded}
    >
      {content}
    </button>
  ) : (
    <div className="tablet:pt-0 flex items-center justify-between py-4">{content}</div>
  )
}

/**
 * 移动端子分类内容组件
 */
export const SubSubCategoryContentComp = ({ matchSeriesMetadata }: { matchSeriesMetadata: any }) => {
  const { locale = 'us' } = useAiuiContext()
  return (
    <div className="laptop:gap-3 flex flex-col gap-2">
      {!!matchSeriesMetadata?.series?.length &&
        matchSeriesMetadata?.series?.map((seriesItem: any, pIndex: number) => (
          <div key={`seriesItem-${seriesItem?.label || ''}-${pIndex}`}>
            {seriesItem.label && (
              <Text html={seriesItem.label} as="p" className="mb-2 text-sm font-bold leading-[1.4] text-[#3D3D3F]" />
            )}
            <div className="laptop:grid-cols-3 laptop:gap-4 tablet:grid tablet:grid-cols-2 tablet:gap-3 flex flex-col gap-2">
              {!!matchSeriesMetadata?.banner && (
                <Link
                  asChild={!matchSeriesMetadata?.banner?.href}
                  href={getLocalizedPath(matchSeriesMetadata?.banner?.href, locale)}
                >
                  <div className="relative [&_img]:hover:scale-110 [&_img]:hover:transition-all [&_img]:hover:duration-500">
                    <Picture
                      source={matchSeriesMetadata?.banner?.imageUrl}
                      className="laptop:h-[120px] h-[114px]"
                      imgClassName="object-cover h-full"
                    />
                    <div className="absolute inset-x-0 bottom-0 p-4">
                      <Heading
                        size={2}
                        html={matchSeriesMetadata?.banner?.title || 'Buy in Guide'}
                        className={cn('font-bold text-white', {
                          'text-black': matchSeriesMetadata?.banner?.theme === 'dark',
                        })}
                      />
                      <Text
                        html={matchSeriesMetadata?.banner?.desc || '20.000mAh'}
                        className={cn('text-sm font-bold text-white', {
                          'text-black': matchSeriesMetadata?.banner?.theme === 'dark',
                        })}
                      />
                    </div>
                  </div>
                </Link>
              )}
              {seriesItem.products?.map((product: any, index: number) => (
                <SeriesProductItem
                  position={index}
                  key={`seriesProductItem-${seriesItem?.label || ''}-${pIndex}-${index}`}
                  product={product}
                  seriesLabel={matchSeriesMetadata?.label}
                  isCollection={matchSeriesMetadata?.isCollection}
                />
              ))}
            </div>
          </div>
        ))}
    </div>
  )
}
